import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, CreditCard, FileText, Settings, FlaskConical, LogOut, ArrowLeftCircle } from 'lucide-react';
import api from '../api/client';
import GlassTopBar from './GlassTopBar';
import { useTheme } from '../context/ThemeContext';

// Get main platform URL based on environment
const getMainPlatformUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  return 'https://ai.app.platform.halisoft.biz';
};

const PortalLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [user, setUser] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      const email = localStorage.getItem('halisoft_email');
      if (email) {
        try {
          const [userRes, adminRes] = await Promise.all([
            api.get('/api/user/me').catch(() => ({ data: { email } })),
            api.get('/api/auth/check-admin').catch(() => ({ data: { isAdmin: false } }))
          ]);
          setUser(userRes.data);
          setIsAdmin(adminRes.data?.isAdmin || false);
        } catch (e) {
          setUser({ email });
        }
      }
    };
    loadUser();
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (e) { /* ignore */ }
    localStorage.removeItem('halisoft_user');
    localStorage.removeItem('halisoft_email');
    localStorage.removeItem('halisoft_token');
    navigate('/login');
  };

  const getEmailParam = () => {
    const email = user?.email || localStorage.getItem('halisoft_email') || '';
    return email ? `?email=${encodeURIComponent(email)}` : '';
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <Home size={20} /> },
    { path: '/subscription', label: 'Subscription', icon: <CreditCard size={20} /> },
    { path: '/invoices', label: 'Invoices', icon: <FileText size={20} /> },
    { path: '/settings', label: 'Settings', icon: <Settings size={20} /> },
  ];

  const isActive = (path) => location.pathname === path;

  // Theme-aware styles
  const sidebarBg = isDark
    ? 'linear-gradient(180deg, #0f0d1a 0%, #1e1b4b 100%)'
    : 'linear-gradient(180deg, #1e1b4b 0%, #312e81 100%)';

  const mainBg = isDark ? '#0f172a' : '#f3f4f6';
  const contentBg = isDark ? '#1e293b' : 'transparent';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: mainBg }}>
      {/* Sidebar */}
      <aside style={{
        width: sidebarCollapsed ? '70px' : '250px',
        background: sidebarBg,
        color: 'white',
        transition: 'width 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 100
      }}>
        {/* Logo */}
        <div style={{
          padding: '1.25rem',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          minHeight: '5rem'
        }}>
          {!sidebarCollapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.5rem' }}>🧪</span>
              <span style={{ fontWeight: '700', fontSize: '1.25rem' }}>HaliLab</span>
            </div>
          )}
          {sidebarCollapsed && <span style={{ fontSize: '1.5rem' }}>🧪</span>}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '1rem 0.75rem' }}>
          {/* Back to Main Platform */}
          <button
            onClick={() => {
              const email = user?.email || localStorage.getItem('halisoft_email') || '';
              const mainUrl = getMainPlatformUrl();
              window.location.href = `${mainUrl}/customer/dashboard${email ? `?email=${encodeURIComponent(email)}` : ''}`;
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: sidebarCollapsed ? '0.75rem' : '0.75rem 1rem',
              marginBottom: '1rem',
              background: 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(139,92,246,0.2))',
              border: '1px solid rgba(139,92,246,0.3)',
              borderRadius: '10px',
              color: '#c4b5fd',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(99,102,241,0.4), rgba(139,92,246,0.3))';
              e.currentTarget.style.transform = 'translateX(2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(139,92,246,0.2))';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
            title="Back to Main Platform"
          >
            <ArrowLeftCircle size={20} />
            {!sidebarCollapsed && <span>Main Platform</span>}
          </button>

          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path + getEmailParam())}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: sidebarCollapsed ? '0.75rem' : '0.75rem 1rem',
                marginBottom: '0.5rem',
                background: isActive(item.path) ? 'rgba(255,255,255,0.15)' : 'transparent',
                border: 'none',
                borderRadius: '10px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: isActive(item.path) ? '600' : '400',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => { if (!isActive(item.path)) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
              onMouseOut={(e) => { if (!isActive(item.path)) e.currentTarget.style.background = 'transparent'; }}
            >
              {item.icon}
              {!sidebarCollapsed && <span>{item.label}</span>}
            </button>
          ))}

          {isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: sidebarCollapsed ? '0.75rem' : '0.75rem 1rem',
                marginTop: '1rem',
                background: 'rgba(239,68,68,0.2)',
                border: 'none',
                borderRadius: '10px',
                color: '#fca5a5',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start'
              }}
            >
              <FlaskConical size={20} />
              {!sidebarCollapsed && <span>Admin</span>}
            </button>
          )}
        </nav>

        {/* User & Logout */}
        <div style={{
          padding: '1rem',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          {!sidebarCollapsed && user && (
            <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', opacity: 0.8 }}>
              {user.email}
            </div>
          )}
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: sidebarCollapsed ? '0.75rem' : '0.75rem 1rem',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.9rem',
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start'
            }}
          >
            <LogOut size={20} />
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{
        flex: 1,
        marginLeft: sidebarCollapsed ? '70px' : '250px',
        transition: 'margin-left 0.3s ease',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Glass Top Bar */}
        <GlassTopBar
          brandName="HaliLab"
          brandSubtitle="Customer Portal"
          userEmail={user?.email || ''}
          darkMode={isDark}
          toggleDarkMode={toggleTheme}
          isSidebarCollapsed={sidebarCollapsed}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          onLogout={handleLogout}
        />

        {/* Page Content */}
        <div style={{
          padding: '1.5rem',
          background: contentBg,
          flex: 1
        }}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default PortalLayout;
